import { useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import {
  BadgeCheck,
  Bell,
  Bookmark,
  ListPlus,
  PanelLeftClose,
  PanelLeftOpen,
  Play,
  Plus,
  TextSearch,
  type LucideIcon,
} from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { paletteFromTitle } from '@shared/mappers';
import type { UserList, UserShowStatus } from '@shared/types';

import { useAuth } from '@/auth/authContext';
import { BrandMark } from '@/components/BrandMark';
import {
  isApplePlatform,
  isTextField,
  paletteShortcut,
  preloadCommandPalette,
  useCommandPalette,
} from '@/components/CommandPalette';
import { formatUnreadBadge, RAIL_ITEMS } from '@/components/nav-items';
import { Poster, POSTER_SIZES } from '@/components/Poster';
import { Reveal } from '@/components/Reveal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useWholeLibrary } from '@/features/library/api';
import { useLists } from '@/features/lists/api';
import { useUnreadCount } from '@/features/notif/api';
import { useImportExportGate, useMyProfile } from '@/features/profile/api';
import { initialsFor } from '@/features/profile/format';
import { ImportExportWizard } from '@/features/profile/ImportExportWizard';
import { useDiscussionsBadge } from '@/features/tonight/nav-badges';
import { useUpNext } from '@/features/upnext/api';
import { UpNextProgress } from '@/features/upnext/UpNextCard';
import type { UpNextItem } from '@/features/upnext/model';
import { useUpNextEntry } from '@/features/upnext/useUpNextEntry';
import { useLocaleOptional } from '@/i18n/locale';
import { pressable } from '@/lib/motion';
import { useMediaQuery } from '@/lib/use-media-query';
import { cn } from '@/lib/utils';
import { ThemeContext, type ThemePreference } from '@/theme/theme';

const MotionNavLink = motion.create(NavLink);

/** Per-viewer convenience: the lg+ rail's collapsed state (ui-ux.md §3). */
const COLLAPSE_STORAGE_KEY = 'screenatlas.rail.collapsed.v1';

function loadCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage blocked: the choice lasts for this visit only.
  }
}

/** `⌘\` on Apple platforms, `Ctrl \` elsewhere. */
function collapseShortcut(): { label: string; aria: string } {
  return isApplePlatform()
    ? { label: '⌘\\', aria: 'Meta+\\' }
    : { label: 'Ctrl \\', aria: 'Control+\\' };
}

/**
 * The viewer's collapse preference, persisted, plus its shortcuts: ⌘\ /
 * Ctrl+\ anywhere, and `[` when focus is not in a text field. Shortcuts only
 * act where the toggle exists (lg+); md is always the icon rail.
 */
function useRailCollapse(enabled: boolean): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  useEffect(() => saveCollapsed(collapsed), [collapsed]);
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey) return;
      const modified = event.metaKey || event.ctrlKey;
      if ((modified && event.key === '\\') || (!modified && event.key === '[' && !isTextField(event.target))) {
        event.preventDefault();
        setCollapsed((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
  return [collapsed, () => setCollapsed((current) => !current)];
}

/**
 * The unread count dot pinned to an icon's top-right (collapsed rail, bell).
 * Coral (`danger`), not amber: amber is reserved for the view's next action
 * (ui-ux.md §2).
 */
function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      aria-hidden="true"
      className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-micro tabular-nums text-on-danger"
    >
      {formatUnreadBadge(count)}
    </span>
  );
}

/** A collapsed-rail control gets its label as a right-side tooltip. */
function RailTooltip({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: ReactNode;
  children: ReactElement;
}) {
  if (!collapsed) return children;
  // The trigger is a wrapper, never the control itself: Radix's Slot merges
  // className as strings, which breaks NavLink's `className={({ isActive }) => …}`
  // function (the collapsed rows lost their height, padding and active pill).
  // Hover and focus bubble up from the control, so the tooltip still opens.
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <span className="flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The notifications bell (follow events; replies live in Discussions): a
 * round icon link to `/notifications` with the coral unread-count badge from
 * `useUnreadCount`. Sits beside the account menu on the rail and in Tonight's
 * masthead on mobile, where the tab bar has no Alerts tab.
 */
export function NotificationsBell({ className }: { className?: string }) {
  const unread = useUnreadCount();
  const count = unread.data ?? 0;
  const { t } = useLocaleOptional();
  const label = t('nav.notifications');
  return (
    <MotionNavLink
      to="/notifications"
      aria-label={count > 0 ? t('nav.notificationsUnread', { count: formatUnreadBadge(count) }) : label}
      className={({ isActive }) =>
        cn(
          'touch-target-min relative inline-flex size-10 shrink-0 items-center justify-center rounded-full transition-colors duration-150',
          isActive
            ? 'bg-accent-soft text-ink [&_svg]:text-accent-ink'
            : 'text-ink-secondary hover:bg-surface-alt hover:text-ink',
          className,
        )
      }
      {...pressable()}
    >
      <span className="relative">
        <Bell className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        {count > 0 && <UnreadBadge count={count} />}
      </span>
    </MotionNavLink>
  );
}

/** Prefetch the lazy palette chunk; a failed prefetch retries on open. */
function warmPalette() {
  preloadCommandPalette().catch(() => {});
}

/** One rail row's box: 36px, an 18px icon column that lines up expanded and collapsed. */
const ROW = 'relative flex h-9 items-center gap-3 rounded px-[11px] text-subhead transition-colors duration-fast';
const ROW_IDLE = 'text-ink-secondary hover:bg-surface-alt hover:text-ink';
const ICON = 'size-[18px] shrink-0';

/**
 * The field-like search button under the brand: the rail's most prominent
 * control, but neutral — recessed into the panel (`bg-bg` + a 1px `border`
 * outline), never amber. Opens the shell's ⌘K palette; collapsed it is the
 * icon alone with a tooltip.
 */
function SearchButton({ collapsed }: { collapsed: boolean }) {
  const { setOpen } = useCommandPalette();
  const { t } = useLocaleOptional();
  const shortcut = paletteShortcut();
  return (
    <RailTooltip collapsed={collapsed} label={t('nav.searchTooltip', { shortcut: shortcut.label })}>
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        // Intent to search: fetch the palette chunk before the click lands.
        onPointerEnter={warmPalette}
        onFocus={warmPalette}
        aria-label={t('nav.searchA11y', { shortcut: shortcut.label })}
        aria-keyshortcuts={shortcut.aria}
        className="flex h-9 w-full items-center gap-2.5 rounded-md border border-border bg-bg px-[10px] text-subhead text-ink-secondary shadow-highlight transition-colors duration-fast hover:text-ink"
        {...pressable()}
      >
        <TextSearch className={ICON} strokeWidth={1.75} aria-hidden />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-left">{t('nav.searchField')}</span>
            <kbd className="shrink-0 font-mono text-caption text-timecode text-ink-tertiary">
              {shortcut.label}
            </kbd>
          </>
        )}
      </motion.button>
    </RailTooltip>
  );
}

/** The lg+ collapse toggle: top-right of the rail expanded, under the mark collapsed. */
function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useLocaleOptional();
  const shortcut = collapseShortcut();
  const label = collapsed ? t('nav.expand') : t('nav.collapse');
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onToggle}
          aria-label={label}
          aria-keyshortcuts={shortcut.aria}
          className={cn(
            'touch-target-min inline-flex shrink-0 items-center justify-center rounded text-ink-tertiary transition-colors duration-fast hover:bg-surface-alt hover:text-ink',
            collapsed ? 'h-8 w-10' : 'size-8',
          )}
        >
          <Icon className={ICON} strokeWidth={1.75} aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side={collapsed ? 'right' : 'bottom'}>
        {label} <kbd className="ml-1 text-timecode">{shortcut.label}</kbd>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The persistent rail (ui-ux.md §3 App shell) — a full-height `surface`
 * panel, separated from the canvas by the surface step (plus a faint inset
 * edge in dark), never a border.
 *
 * lg+: 232px expanded, or the 64px icon rail when the viewer collapses it
 * (the toggle, ⌘\ / Ctrl+\, or `[`; persisted per viewer). md: always the
 * 64px icon rail. Mobile: hidden (BottomTabs).
 *
 * Pinned top: brand lockup (→ Tonight) and the search field. Scrolling
 * middle: the five destinations, then (expanded only) "Your shelves" and
 * "Lists". Pinned bottom: the Up next tile and the account row with the
 * bell. The width is the shell's `--rail-w` (index.css), so the content
 * padding and the xl title stage animate with it.
 */
export function NavRail() {
  const discussionsUnread = useDiscussionsBadge();
  const { t } = useLocaleOptional();
  const isLg = useMediaQuery('(min-width: 1024px)');
  const isMdUp = useMediaQuery('(min-width: 768px)');
  const [preferCollapsed, toggleCollapsed] = useRailCollapse(isLg);
  // Below lg the rail is the icon rail (md) or hidden (mobile).
  const collapsed = !isLg || preferCollapsed;

  return (
    <nav
      aria-label={t('nav.primary')}
      data-collapsed={isLg && preferCollapsed ? '' : undefined}
      className="app-rail fixed inset-y-0 left-0 z-40 hidden flex-col overflow-hidden bg-surface shadow-rail-edge md:flex"
    >
      <div className={cn('flex shrink-0 flex-col gap-4 px-3 pb-3 pt-5', collapsed && 'items-start')}>
        <div className={cn('flex items-center justify-between gap-2', collapsed && 'flex-col items-start gap-3')}>
          <NavLink to="/" end className="rounded px-1.5">
            <BrandMark size="sm" showWordmark={!collapsed} />
          </NavLink>
          {isLg && <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />}
        </div>
        <div className={collapsed ? 'w-10' : 'w-full'}>
          <SearchButton collapsed={collapsed} />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-1 [scrollbar-width:thin]">
        <div className="flex flex-col gap-0.5">
          {RAIL_ITEMS.map(({ to, labelKey, icon: Icon, end, showUnreadBadge }) => {
            const count = showUnreadBadge ? discussionsUnread : 0;
            const label = t(labelKey);
            return (
              <RailTooltip key={to} collapsed={collapsed} label={label}>
                <MotionNavLink
                  to={to}
                  end={end}
                  aria-label={
                    count > 0 ? t('nav.unreadReplies', { label, count: formatUnreadBadge(count) }) : label
                  }
                  className={({ isActive }) =>
                    cn(
                      ROW,
                      collapsed && 'w-10',
                      isActive ? 'bg-accent-soft font-semibold text-ink [&_svg]:text-accent-ink' : ROW_IDLE,
                    )
                  }
                  {...pressable()}
                >
                  <span className="relative">
                    <Icon className={ICON} strokeWidth={1.75} aria-hidden />
                    {collapsed && count > 0 && <UnreadBadge count={count} />}
                  </span>
                  {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
                  {!collapsed && count > 0 && (
                    <span
                      aria-hidden="true"
                      className="flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-micro tabular-nums text-on-danger"
                    >
                      {formatUnreadBadge(count)}
                    </span>
                  )}
                </MotionNavLink>
              </RailTooltip>
            );
          })}
        </div>
        {!collapsed && <Shelves />}
      </div>

      <div className={cn('flex shrink-0 flex-col gap-2 px-3 pb-4 pt-3', collapsed && 'items-start')}>
        {isMdUp && <UpNextTile collapsed={collapsed} />}
        <div className={cn('flex items-center gap-1', collapsed && 'flex-col items-start')}>
          <div className={collapsed ? 'w-10' : 'min-w-0 flex-1'}>
            <AccountMenu collapsed={collapsed} />
          </div>
          <RailTooltip collapsed={collapsed} label={t('nav.notifications')}>
            {/* A span trigger: the bell is a plain function component (no ref). */}
            <span className="inline-flex">
              <NotificationsBell />
            </span>
          </RailTooltip>
        </div>
      </div>
    </nav>
  );
}

// --- shelves ------------------------------------------------------------------

const SHELVES: { status: UserShowStatus; labelKey: string; icon: LucideIcon }[] = [
  { status: 'watchlist', labelKey: 'library.filter.watchlist', icon: Bookmark },
  { status: 'watching', labelKey: 'library.filter.watching', icon: Play },
  { status: 'completed', labelKey: 'library.filter.completed', icon: BadgeCheck },
];

/** Lists shown in the rail; the rest are a "All lists" row away. */
const RAIL_LIST_LIMIT = 5;

function SectionHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="mb-2 mt-6 px-[11px] text-caption text-ink-tertiary">
      {children}
    </h2>
  );
}

/** Right-aligned tabular count (shelves, lists). */
function RowCount({ value }: { value: number }) {
  return <span className="shrink-0 text-caption tabular-nums text-ink-tertiary">{value}</span>;
}

/**
 * "Your shelves" (expanded rail only): Watchlist / Watching / Completed deep
 * links into Library's status lens, counted from the whole-library cache
 * (`useWholeLibrary`, shared with Up next and the shelf — no request of its
 * own). Counts appear only once every page is in (`isComplete`); a partial
 * count would be a wrong number. Then "Lists": the first five collections
 * and a "New list" row; the part is absent when there are no lists.
 */
function Shelves() {
  const { t } = useLocaleOptional();
  const { pathname, search } = useLocation();
  const library = useWholeLibrary();
  const lists = useLists();

  const counts = useMemo(() => {
    if (!library.data?.isComplete) return null;
    const out: Partial<Record<UserShowStatus, number>> = {};
    for (const item of library.data.items) out[item.status] = (out[item.status] ?? 0) + 1;
    return out;
  }, [library.data]);

  const activeStatus = pathname === '/library' ? new URLSearchParams(search).get('status') : null;
  const allLists = lists.data ?? [];

  return (
    <>
      <section aria-labelledby="rail-shelves">
        <SectionHeading id="rail-shelves">{t('nav.shelves')}</SectionHeading>
        <ul className="flex flex-col gap-0.5">
          {SHELVES.map(({ status, labelKey, icon: Icon }) => {
            const label = t(labelKey);
            const count = counts ? (counts[status] ?? 0) : null;
            const active = activeStatus === status;
            return (
              <li key={status}>
                <Link
                  to={`/library?status=${status}`}
                  aria-current={active ? 'page' : undefined}
                  aria-label={count === null ? label : t('nav.shelfCount', { label, count })}
                  className={cn(ROW, active ? 'bg-surface-alt text-ink' : ROW_IDLE)}
                >
                  <Icon className={ICON} strokeWidth={1.75} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{label}</span>
                  {count !== null && <RowCount value={count} />}
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      {allLists.length > 0 && (
        <section aria-labelledby="rail-lists">
          <SectionHeading id="rail-lists">{t('lists.title')}</SectionHeading>
          <ul className="flex flex-col gap-0.5">
            {allLists.slice(0, RAIL_LIST_LIMIT).map((list) => {
              const active = pathname === `/lists/${list.id}`;
              return (
                <li key={list.id}>
                  <Link
                    to={`/lists/${list.id}`}
                    aria-current={active ? 'page' : undefined}
                    aria-label={t('nav.listCount', { name: list.name, count: list.showCount })}
                    className={cn(ROW, active ? 'bg-surface-alt text-ink' : ROW_IDLE)}
                  >
                    <ListCovers list={list} />
                    <span className="min-w-0 flex-1 truncate">{list.name}</span>
                    <RowCount value={list.showCount} />
                  </Link>
                </li>
              );
            })}
            {allLists.length > RAIL_LIST_LIMIT && (
              <li>
                <Link to="/lists" className={cn(ROW, ROW_IDLE)}>
                  <span className={ICON} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{t('nav.allLists')}</span>
                  <RowCount value={allLists.length} />
                </Link>
              </li>
            )}
            <li>
              {/* Opens /lists' own create dialog (router state, not the URL). */}
              <Link to="/lists" state={{ createList: true }} className={cn(ROW, 'text-ink-tertiary hover:bg-surface-alt hover:text-ink')}>
                <Plus className={ICON} strokeWidth={1.75} aria-hidden />
                <span className="min-w-0 flex-1 truncate">{t('lists.newList')}</span>
              </Link>
            </li>
          </ul>
        </section>
      )}
    </>
  );
}

/**
 * A list's tiny cover in the 18px icon column: two members stacked with an
 * offset, one member alone, or the Lists glyph when it is empty.
 */
function ListCovers({ list }: { list: UserList }) {
  const covers = (list.preview ?? []).slice(0, 2);
  if (covers.length === 0) return <ListPlus className={ICON} strokeWidth={1.75} aria-hidden />;
  const thumb = (index: number, className: string) => {
    const item = covers[index];
    return (
      <Poster
        key={item.showId}
        id={item.showId}
        name=""
        palette={paletteFromTitle(item.title)}
        imageUrl={item.posterUrl}
        imageFallbackUrl={item.posterFallbackUrl}
        sizes={POSTER_SIZES.thumb}
        layout={false}
        fallbackTitle={false}
        className={cn('pointer-events-none absolute w-3.5 rounded-[2px]', className)}
      />
    );
  };
  return (
    <span aria-hidden className="relative h-6 w-[18px] shrink-0">
      {covers.length === 2 ? (
        <>
          {thumb(1, 'right-0 top-0 opacity-70')}
          {thumb(0, 'bottom-0 left-0 shadow-sm')}
        </>
      ) : (
        thumb(0, 'left-0.5 top-0.5')
      )}
    </span>
  );
}

// --- up next ------------------------------------------------------------------

/**
 * The first Up next title as a resume tile pinned above the account row:
 * a recessed `bg-bg` block with a 32×48 cover, the name, the next `S1·E3`
 * and the thin progress bar, linking to the show (no mark-watched here —
 * the rail stays neutral). Collapsed it is the cover alone with a tooltip.
 * Absent while loading and when nothing is in progress; it settles in with
 * `Reveal` when its data arrives.
 */
function UpNextTile({ collapsed }: { collapsed: boolean }) {
  const { items } = useUpNext(1);
  const item = items[0];
  if (!item) return null;
  return (
    <Reveal key={item.title.id} className={collapsed ? undefined : 'w-full'}>
      <UpNextTileBody item={item} collapsed={collapsed} />
    </Reveal>
  );
}

function UpNextTileBody({ item, collapsed }: { item: UpNextItem; collapsed: boolean }) {
  const { t } = useLocaleOptional();
  const entry = useUpNextEntry(item);
  const label = entry.next
    ? t('nav.upNextA11y', { title: entry.name, code: entry.code })
    : t('nav.upNextTitleA11y', { title: entry.name });
  const cover = (
    <Poster
      id={entry.id}
      name=""
      palette={item.title.palette}
      imageUrl={item.title.posterUrl ?? undefined}
      imageFallbackUrl={item.title.posterFallbackUrl ?? undefined}
      sizes={POSTER_SIZES.thumb}
      layout={false}
      fallbackTitle={false}
      className="pointer-events-none w-8 shrink-0"
    />
  );

  if (collapsed) {
    return (
      <RailTooltip collapsed label={label}>
        <Link to={`/show/${entry.id}`} aria-label={label} className="block w-10 rounded-sm px-1">
          {cover}
        </Link>
      </RailTooltip>
    );
  }

  return (
    <Link
      to={`/show/${entry.id}`}
      aria-label={label}
      className="flex items-center gap-3 rounded-lg bg-bg p-2 transition-colors duration-fast hover:bg-surface-alt"
    >
      {cover}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-caption font-semibold text-ink">{entry.name}</span>
        <span className="min-h-4 text-caption text-ink-secondary">
          {entry.next ? <span className="text-timecode">{entry.compactCode}</span> : null}
        </span>
        <UpNextProgress
          name={entry.name}
          watched={entry.progress.watched}
          total={entry.progress.total}
          percent={entry.percent}
        />
      </span>
    </Link>
  );
}

// --- account ------------------------------------------------------------------

const THEME_OPTIONS: { value: ThemePreference; labelKey: string }[] = [
  { value: 'light', labelKey: 'theme.light' },
  { value: 'system', labelKey: 'theme.system' },
  { value: 'dark', labelKey: 'theme.dark' },
];

type PendingDialog = 'importExport' | 'shortcuts' | null;

/**
 * The account menu pinned to the bottom of the rail — the standard sidebar
 * pattern (Slack/Linear/Notion): avatar + display name + @handle that opens
 * a menu instead of navigating. Collapsed: avatar only, the accessible name
 * carrying the identity. The menu: View profile, Settings, Import / export
 * (behind `import_export_v1`; opens `ImportExportWizard`), the theme as
 * Light / System / Dark radio items, Keyboard shortcuts, Sign out.
 *
 * The dialogs open from the menu's close (`onCloseAutoFocus`), after focus
 * is back on the trigger, so they return focus there when they close.
 * A pending profile shows a skeleton so the row doesn't shift; a failed load
 * renders nothing (silent degradation).
 */
function AccountMenu({ collapsed }: { collapsed: boolean }) {
  const profile = useMyProfile();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useLocaleOptional();
  // Optional so the rail renders outside a ThemeProvider (tests, previews).
  const theme = useContext(ThemeContext);
  const importExportGate = useImportExportGate();
  const showImportExport = importExportGate.data === 'on';
  const [signingOut, setSigningOut] = useState(false);
  const [dialog, setDialog] = useState<PendingDialog>(null);
  const pending = useRef<PendingDialog>(null);

  if (profile.isPending) {
    return (
      <div className="flex h-10 items-center gap-3 px-1">
        <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
        {!collapsed && <Skeleton className="h-4 w-24" />}
      </div>
    );
  }

  if (profile.isError || !profile.data) return null;

  const { displayName, username, avatarUrl } = profile.data;
  const name = displayName?.trim() || username;
  const label = displayName?.trim() || `@${username}`;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login', { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <RailTooltip collapsed={collapsed} label={label}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('nav.accountMenu', { name: label })}
              className="flex h-10 w-full min-w-0 items-center gap-3 rounded px-1 text-left transition-colors duration-fast hover:bg-surface-alt"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={avatarUrl ?? undefined} alt="" />
                <AvatarFallback>{initialsFor(name)}</AvatarFallback>
              </Avatar>
              {!collapsed && (
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-subhead text-ink">{name}</span>
                  <span className="truncate text-caption text-ink-tertiary">@{username}</span>
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
        </RailTooltip>
        <DropdownMenuContent
          side="right"
          align="end"
          className="min-w-[13rem]"
          onCloseAutoFocus={() => {
            // Focus is about to return to the trigger; open the chosen dialog
            // after it, so the dialog restores focus there on close.
            if (pending.current) setDialog(pending.current);
            pending.current = null;
          }}
        >
          <DropdownMenuItem onSelect={() => navigate('/profile')}>{t('nav.viewProfile')}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate('/settings')}>{t('nav.settings')}</DropdownMenuItem>
          {showImportExport && (
            <DropdownMenuItem onSelect={() => {
                pending.current = 'importExport';
              }}>
              {t('nav.importExport')}
            </DropdownMenuItem>
          )}
          {theme && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-caption font-normal text-ink-tertiary">
                {t('theme.label')}
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={theme.preference}
                onValueChange={(value) => theme.setPreference(value as ThemePreference)}
              >
                {THEME_OPTIONS.map(({ value, labelKey }) => (
                  // Keep the menu open: a theme change is previewed in place.
                  <DropdownMenuRadioItem key={value} value={value} onSelect={(e) => e.preventDefault()}>
                    {t(labelKey)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => {
              pending.current = 'shortcuts';
            }}>
            {t('nav.shortcuts')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={signingOut} onSelect={() => void handleSignOut()}>
            {t('profile.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showImportExport && (
        <ImportExportWizard
          open={dialog === 'importExport'}
          onOpenChange={(open) => setDialog(open ? 'importExport' : null)}
        />
      )}
      <ShortcutsDialog
        open={dialog === 'shortcuts'}
        onOpenChange={(open) => setDialog(open ? 'shortcuts' : null)}
      />
    </>
  );
}

/**
 * The shortcuts that exist, and nothing else: the palette (⌘K / Ctrl K, `/`),
 * the rail collapse (⌘\ / Ctrl \, `[`) and the episode-list keys (J / K / X,
 * `Seasons.tsx`).
 */
function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useLocaleOptional();
  const groups: { heading?: string; rows: [string[], string][] }[] = [
    {
      rows: [
        [[paletteShortcut().label, '/'], t('nav.shortcutSearch')],
        [[collapseShortcut().label, '['], t('nav.shortcutCollapse')],
      ],
    },
    {
      heading: t('nav.shortcutEpisodes'),
      rows: [
        [['J'], t('show.shortcuts.next')],
        [['K'], t('show.shortcuts.previous')],
        [['X'], t('show.shortcuts.toggle')],
      ],
    },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('nav.shortcuts')}</DialogTitle>
          <DialogDescription>{t('nav.shortcutsDescription')}</DialogDescription>
        </DialogHeader>
        {groups.map(({ heading, rows }) => (
          <div key={heading ?? 'global'} className="flex flex-col gap-2">
            {heading && <h3 className="text-caption text-ink-tertiary">{heading}</h3>}
            <dl className="m-0 grid grid-cols-[1fr_auto] items-center gap-x-6 gap-y-2">
              {rows.map(([keys, label]) => (
                <div key={label} className="contents">
                  <dt className="text-subhead text-ink">{label}</dt>
                  <dd className="m-0 flex justify-end gap-1.5">
                    {keys.map((key) => (
                      <kbd
                        key={key}
                        className="rounded-sm bg-surface-alt px-1.5 font-mono text-caption text-timecode text-ink-secondary"
                      >
                        {key}
                      </kbd>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
